// Historical track filter used by the Auto "Cull by best ROI track" button and review tint.
// Venues are ranked by archived 2020-2022 Core rank 1-3 winner counts (see trackFilterRanking.json).
// Each bucket keeps only its tested top 1, 2 or 3 venues racing that day (historicalTrackFilterReference.json).
import ranking from './trackFilterRanking.json';
import reference from './historicalTrackFilterReference.json';

export const CULL_POLICIES: Record<string, { tracks: number; label: string }> = Object.fromEntries(
  (reference as any).rows.map((r: any) => [r.id, { tracks: Number(r.tracks), label: String(r.label) }]),
);


const scores: Record<string, number> = Object.fromEntries((ranking as any).venues.map((v: any) => [v.key, Number(v.total_wins)]));
const aliases: Record<string, string> = (ranking as any).aliases || {};

// Same normalisation as the research evaluator: uppercase, MOUNT -> MT, strip non-alphanumerics, then aliases.
export function venueKey(name: string): string {
  const n = String(name || '').toUpperCase().replace(/MOUNT/g, 'MT').replace(/[^A-Z0-9]/g, '');
  return aliases[n] || n;
}

export function sydneyDate(iso: string): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Australia/Sydney', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(iso));
}

// Rank the day's meetings by fixed historical score; exact ties alphabetical. Unranked venues cannot be retained.
export function rankDay(meetingNames: string[]): string[] {
  const keys = Array.from(new Set(meetingNames.filter(n => !/TRIAL/i.test(n)).map(venueKey))).filter(k => k in scores);
  return keys.sort((a, b) => (scores[b] - scores[a]) || (a < b ? -1 : a > b ? 1 : 0));
}

export async function loadDayOrders(dates: string[], apiBase: string): Promise<Record<string, string[]>> {
  const out: Record<string, string[]> = {};
  for (const date of Array.from(new Set(dates.filter(Boolean)))) {
    const res = await fetch(`${apiBase}/api/meetings/date/${encodeURIComponent(date)}`);
    if (!res.ok) throw new Error(`meeting list for ${date} returned ${res.status}`);
    const body = await res.json();
    const meetings = Array.isArray(body) ? body : (body.meetings || []);
    if (!meetings.length) throw new Error(`no meetings found for ${date}`);
    out[date] = rankDay(meetings.map((m: any) => String(m.meeting_name || m.venue || '')));
  }
  return out;
}

export type VenueCheck = { tracks: number; rank: number | null; of: number; keep: boolean };

export function checkRow(row: any, orders: Record<string, string[]>): VenueCheck | null {
  const policy = CULL_POLICIES[row?.policy_id];
  if (!policy || !row?.start_time) return null;
  const order = orders[sydneyDate(row.start_time)];
  if (!order) return null;
  const idx = order.indexOf(venueKey(row.track));
  const rank = idx >= 0 ? idx + 1 : null;
  return { tracks: policy.tracks, rank, of: order.length, keep: rank !== null && rank <= policy.tracks };
}
